import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { loadPersisted, savePersisted } from './_persist';
import { log } from '@/lib/log';
import type { RecordingsFilterState } from '@/lib/api';
// TYPE-only (erased at build — no runtime cycle with externalPlaylist.ts,
// which imports this store). This buys ONE thing: the union is spelled once,
// beside the code that turns a scheme into a link, instead of twice. It does
// NOT make the two impossible to desync — the link builder takes `string`
// (the value is read back from localStorage) and nothing switches
// exhaustively — so the actual guard is the runtime `EXTERNAL_PLAYER_SCHEMES`
// list and the mirror tests in `externalPlaylist.test.ts`.
import type { ExternalPlayerScheme } from '@/lib/player/externalPlaylist';

// Cross-page UI preferences — sidebar collapse, language filters, EPG group
// memory, plot search toggles, dismissed banners. ONE localStorage key
// (`nodecast.filters`) replaces the legacy SPA's 14-key sprawl.

interface FiltersShape {
    sidebarCollapsed: boolean;
    /**
     * Multi-select language filter for the Live channel sidebar.
     * Empty array = no filter (show all). Each entry is an ISO-639-1
     * code matching `Channel.language`. Persists across sessions.
     * Replaces the legacy single-string `channelLanguageFilter` (carried
     * over by the v5→v6 migration).
     */
    channelLanguageFilters: string[];
    /**
     * Multi-select country/region filter for the Live channel sidebar.
     * Each entry is the prefix taken from `Channel.groupTitle` before
     * the first `|` (e.g. "DE", "US", "AFR"). Empty = no filter.
     */
    channelCountryFilters: string[];
    /** Atlas Phase 6 — the user's SAVED default region facet (spec §5.1). The
     *  active `channelCountryFilters` is "dirty" when it differs from this. */
    channelCountryFiltersSaved: string[];
    /** Atlas R3 — best-effort TAGS facet (RAW/VIP/HD/4K/UHD…), parsed from channel
     *  names by `lib/channelTags.ts`. Provider-claimed, never verified (§2.1).
     *  Empty = no filter; a channel matches if it carries ANY selected tag. */
    channelTagFilters: string[];
    /** Atlas R3 — the user's SAVED default tags facet; active is "dirty" when ≠. */
    channelTagFiltersSaved: string[];
    /** MEASURED-quality facet (observed data, not provider claims): only show
     *  channels whose last-seen probe/playback measured at least this video
     *  height (720/1080/2160). null = no filter. Channels without ANY
     *  measurement are excluded while active — that's the point ("show me
     *  measured-good streams"); the group bulk-probe fills the data in. */
    channelMinHeight: number | null;
    /** MEASURED-quality facet — minimum last-seen fps (30/50). null = off.
     *  The sports facet: 1080p25 ≠ 1080p50. */
    channelMinFps: number | null;
    /** MEASURED-tracks facet — only channels last seen carrying 2+ audio
     *  tracks (the dubbed/original pairs worth switching between). Same
     *  blind spot as the two above and for the same reason: a channel
     *  nobody has tuned has no measurement, so it cannot match. */
    channelMultiAudioOnly: boolean;
    /** "Open in external player" hand-off — DEVICE-LOCAL on purpose: the right
     *  player differs per machine (IINA on a Mac, PotPlayer on Windows, Just
     *  Player on a phone), so this lives in localStorage, not server settings.
     *  'm3u' downloads a one-entry playlist (desktop only — see the Android
     *  block in `lib/player/externalPlaylist.ts`); the desktop presets
     *  deep-link via the player's URL scheme; the Android presets hand the
     *  stream URL to a named package via a Chrome `intent:` URI. Because the
     *  value is per-device, the presets are labelled by platform and NOTHING
     *  sniffs the user agent. No schema VERSION bump — additive widening of a
     *  string union (the `player.aspectRatio` / Phase 4 retro #3 model): a
     *  stored 'm3u'/'iina'/'vlc'/'potplayer'/'custom' still means what it
     *  meant, and a same-version read returns the blob verbatim. */
    externalPlayerScheme: ExternalPlayerScheme;
    /** Custom app-scheme template with a {url} placeholder (e.g. the
     *  mpv-handler convention "mpv://play/{url}"). Only used when
     *  `externalPlayerScheme === 'custom'`. */
    externalPlayerCustomTemplate: string;
    /** Atlas R5 — "Use my group layout" (Manage Groups tab). ON = user group order +
     *  hidden + local groups; OFF = provider order, read-only, local groups
     *  suppressed-not-deleted. Optional (no migration): consumers read `?? true`. */
    useMyGroupLayout?: boolean;
    epgSelectedGroup: string | null;
    epgLanguageFilter: string | null;
    epgHideNoData: boolean;
    moviesLanguageFilter: string | null;
    moviesSearchPlot: boolean;
    seriesLanguageFilter: string | null;
    seriesSearchPlot: boolean;
    collapsedGroups: string[];
    /**
     * One-shot init flag — `false` means `loadChannels` should auto-collapse
     * every non-Favourites group on the next run, then flip this true.
     * Mirrors legacy `ChannelList.js:391-401`: a 5,000-channel/30-group
     * playlist renders ~30 header rows on first load instead of 5,030.
     * Once the user has expanded/collapsed any group themselves, the flag
     * stays true and we never re-auto-collapse.
     */
    collapsedGroupsInitialized: boolean;
    aspectRatio: 'auto' | '16x9' | '4x3' | 'fill' | 'crop' | 'original';
    /**
     * Per-source-per-expiry dismissal map. Key is `${sourceId}:${expiresAtMs}`,
     * value is the epoch ms the user clicked Dismiss. Phase 1 typed this as a
     * boolean by mistake; Phase 2.3 widened it to the legacy shape and bumped
     * `VERSION` to 2 so older blobs migrate cleanly.
     */
    expiringBannerDismissed: Record<string, number>;
    installPromptDismissed: boolean;
    /**
     * Phase 2.6 — Guide page sidebar width in px, clamped 150..500. Persists across
     * sessions; legacy `EpgGuide.js:116-149` only set the CSS var, never persisted.
     */
    epgSidebarWidth: number;
    /**
     * Mini-player tile dimensions in px when the player is shown in the
     * bottom-right corner (any non-/live route). User-resizable via the
     * grip handle on the tile; persisted across sessions. Clamped to the
     * MINI_PLAYER_BOUNDS range below.
     */
    miniPlayerWidth: number;
    miniPlayerHeight: number;
    /**
     * The channel the user was watching last. Restored on page reload
     * (App.vue's onMounted), so a refresh continues with the same
     * channel even if the URL doesn't carry the deep-link query (e.g.
     * the user reloaded while on /settings). `null` once the user
     * explicitly closes the mini player.
     */
    lastChannel: { sourceId: number; channelId: string } | null;
    /**
     * Spec D — Recordings page filter + sort state. New in v7. Empty
     * statuses array means "no status filter" (= show every status); the
     * dropdowns are null when "All …" is picked. The `<RecordingsFilters>`
     * component is the only writer; the page reads through computed
     * `*Filtered` getters on `useRecordingsStore`.
     */
    recordings: RecordingsFilterState;
    /**
     * Phase post-4.10 — collapsed state for the `<RecommendedEpgSources>`
     * admin panel on the Sources page. `false` (default) = panel rendered
     * expanded on first visit; flips `true` after the user explicitly
     * collapses it. Additive boolean; the v8→v9 migration treats it as
     * default-false for existing blobs.
     */
    recommendedEpgPanelCollapsed: boolean;
    /**
     * Desktop sidebar (AppRail) expanded state. `false` = collapsed 64px icon
     * rail (default); `true` = ~220px labeled sidebar. Desktop-only — the rail
     * is `display:none` below --bp-lg. Distinct from `sidebarCollapsed` (which
     * is the Live *channel* sidebar). Additive boolean; the v14→v15 migration
     * defaults it false for existing blobs.
     */
    navExpanded: boolean;
    /**
     * X4 (ux-remediation #5) — Home's failed-recording banner dismissal.
     * ONE scalar, not a per-row constellation: the epoch ms of the newest
     * `started_at` among failed rows the user has already dismissed. A
     * failed row is shown again only once its `started_at` is NEWER than
     * this — i.e. dismissing always covers everything failed so far, and a
     * later failure re-arms the banner. `0` = never dismissed.
     */
    recordingFailuresDismissedAt: number;
    /**
     * Live's CHANNEL LIST pane, collapsed. Large-mobile only (the pane header
     * that toggles it is rendered at that tier): `false` = the list is beside
     * the stage, `true` = the stage takes the width and a 44px `‹ Channels`
     * edge tab is the way back. Default `false` — book-portrait is a browsing
     * posture (mobile-foldable-redesign §1.1), and fullscreen is one tap away
     * for watching.
     *
     * Distinct from `sidebarCollapsed`, which is the DESKTOP Live sidebar, and
     * from `navExpanded`, which is the AppRail.
     */
    liveListPaneCollapsed: boolean;
}

export const RECORDINGS_FILTER_DEFAULTS: RecordingsFilterState = {
    statuses: [],
    channelKey: null,
    sourceId: null,
    search: '',
    sort: 'date-desc',
    storagePanelCollapsed: false,
};

const DEFAULTS: FiltersShape = {
    sidebarCollapsed: false,
    channelLanguageFilters: [],
    channelCountryFilters: [],
    channelCountryFiltersSaved: [],
    channelTagFilters: [],
    channelTagFiltersSaved: [],
    channelMinHeight: null,
    channelMinFps: null,
    channelMultiAudioOnly: false,
    externalPlayerScheme: 'm3u',
    externalPlayerCustomTemplate: '',
    useMyGroupLayout: true,
    epgSelectedGroup: 'Favourites',
    epgLanguageFilter: null,
    epgHideNoData: false,
    moviesLanguageFilter: null,
    moviesSearchPlot: false,
    seriesLanguageFilter: null,
    seriesSearchPlot: false,
    collapsedGroups: [],
    collapsedGroupsInitialized: false,
    aspectRatio: 'auto',
    expiringBannerDismissed: {},
    installPromptDismissed: false,
    epgSidebarWidth: 280,
    miniPlayerWidth: 360,
    miniPlayerHeight: 202,
    lastChannel: null,
    recordings: { ...RECORDINGS_FILTER_DEFAULTS },
    recommendedEpgPanelCollapsed: false,
    navExpanded: false,
    recordingFailuresDismissedAt: 0,
    liveListPaneCollapsed: false,
};

export const MINI_PLAYER_BOUNDS = {
    minWidth: 240,
    minHeight: 135,
    maxWidth: 960,
    maxHeight: 540,
} as const;
export function clampMiniPlayer(w: number, h: number): { w: number; h: number } {
    const ww = Number.isFinite(w)
        ? Math.max(MINI_PLAYER_BOUNDS.minWidth, Math.min(MINI_PLAYER_BOUNDS.maxWidth, Math.round(w)))
        : DEFAULTS.miniPlayerWidth;
    const hh = Number.isFinite(h)
        ? Math.max(MINI_PLAYER_BOUNDS.minHeight, Math.min(MINI_PLAYER_BOUNDS.maxHeight, Math.round(h)))
        : DEFAULTS.miniPlayerHeight;
    return { w: ww, h: hh };
}

const KEY = 'nodecast.filters';
/**
 * Persisted schema version. EXPORTED so the migration tests assert against it
 * instead of retyping the number: every bump used to break seven cases that
 * had nothing to do with the new field, which trains people to edit the
 * assertion rather than read it (root rule "two numbers describing one fact
 * must be derived from each other").
 */
export const VERSION = 19;

const SIDEBAR_MIN = 150;
const SIDEBAR_MAX = 500;
function clampSidebar(n: number): number {
    if (!Number.isFinite(n)) return DEFAULTS.epgSidebarWidth;
    return Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, Math.round(n)));
}

export const LEGACY_KEYS: Array<{ key: string; map: (raw: string) => Partial<FiltersShape> | null }> = [
    { key: 'sidebarCollapsed', map: (raw) => ({ sidebarCollapsed: raw === 'true' || raw === '1' }) },
    {
        // Legacy SPA stored a single language string. We now carry a
        // multi-select array; an existing single value drains as a
        // one-item array so a returning user keeps their preference.
        key: 'channelLanguageFilter',
        map: (raw) => (raw ? { channelLanguageFilters: [raw] } : null),
    },
    { key: 'epgSelectedGroup', map: (raw) => ({ epgSelectedGroup: raw || null }) },
    { key: 'epgLanguageFilter', map: (raw) => ({ epgLanguageFilter: raw || null }) },
    { key: 'epgHideNoData', map: (raw) => ({ epgHideNoData: raw === 'true' || raw === '1' }) },
    { key: 'moviesLanguageFilter', map: (raw) => ({ moviesLanguageFilter: raw || null }) },
    { key: 'moviesSearchPlot', map: (raw) => ({ moviesSearchPlot: raw === 'true' || raw === '1' }) },
    { key: 'seriesLanguageFilter', map: (raw) => ({ seriesLanguageFilter: raw || null }) },
    { key: 'seriesSearchPlot', map: (raw) => ({ seriesSearchPlot: raw === 'true' || raw === '1' }) },
    {
        key: 'nodecast_tv_collapsed_groups',
        map: (raw) => {
            try {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr) && arr.every((s) => typeof s === 'string')) {
                    return { collapsedGroups: arr };
                }
            } catch {
                // ignore parse errors
            }
            return null;
        },
    },
    {
        // Phase post-4.1 — widened from 4 modes to 6 (`+ 'crop' | 'original'`)
        // and normalises legacy colon-form (`'16:9'`, `'4:3'`) which the
        // legacy SPA wrote to `localStorage['player.aspectRatio']` from
        // `aspectMode.js`. No schema VERSION bump — additive widening of a
        // string union (Phase 4 retro #3 model).
        key: 'player.aspectRatio',
        map: (raw) => {
            const normalised =
                raw === '16:9' ? '16x9' : raw === '4:3' ? '4x3' : raw;
            const allowed: ReadonlyArray<FiltersShape['aspectRatio']> = [
                'auto',
                '16x9',
                '4x3',
                'fill',
                'crop',
                'original',
            ];
            return (allowed as readonly string[]).includes(normalised)
                ? { aspectRatio: normalised as FiltersShape['aspectRatio'] }
                : null;
        },
    },
    {
        // Phase 2.6 — Guide page sidebar width in px. Legacy `EpgGuide.js:116-149`
        // only set the CSS var on drag end; this drain handles older deployments
        // that may have written the value to localStorage manually. Clamped 150..500.
        key: 'epgSidebarWidth',
        map: (raw) => {
            const v = parseFloat(raw);
            if (Number.isFinite(v)) return { epgSidebarWidth: clampSidebar(v) };
            return null;
        },
    },
    {
        // Legacy SPA stored a JSON-stringified `{ [sourceId:expiresAt]: dismissedAtMs }`
        // map here. Phase 1's drain treated it as a boolean flag (always false);
        // Phase 2.3 reads the real shape.
        key: 'expiringBannerDismissed',
        map: (raw) => {
            try {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    const out: Record<string, number> = {};
                    for (const [k, v] of Object.entries(parsed)) {
                        if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
                    }
                    return { expiringBannerDismissed: out };
                }
            } catch {
                // ignore parse errors — legacy may have stored 'true' / '1'
            }
            return null;
        },
    },
];

const MIGRATIONS = {
    // v1 → v2: `expiringBannerDismissed` widened from `boolean` to
    // `Record<string, number>`. The boolean was always semantically wrong
    // (whole-banner-dismiss vs. per-source-per-expiry), so we discard it
    // and start the map empty.
    1: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
            expiringBannerDismissed: {},
        };
    },
    // v2 → v3: Phase 2.6 adds `epgSidebarWidth`. Older blobs preserve all other
    // fields and inherit the default width; values outside 150..500 are clamped.
    2: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        const raw = (base as { epgSidebarWidth?: unknown }).epgSidebarWidth;
        const width = typeof raw === 'number' ? clampSidebar(raw) : DEFAULTS.epgSidebarWidth;
        return {
            ...DEFAULTS,
            ...base,
            epgSidebarWidth: width,
        };
    },
    // v3 → v4: adds `collapsedGroupsInitialized`. Existing users have already
    // expanded/collapsed groups to taste — preserve their `collapsedGroups`
    // and treat the flag as already-initialized, so we don't surprise them
    // by auto-collapsing on next load. Fresh installs land on default
    // (`false`) and trigger the legacy-style auto-collapse.
    3: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
            collapsedGroupsInitialized: true,
        };
    },
    // v4 → v5: adds mini-player width/height + lastChannel. Existing blobs
    // inherit defaults; user can resize via the grip and the next channel
    // selection populates lastChannel naturally.
    4: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
            miniPlayerWidth: DEFAULTS.miniPlayerWidth,
            miniPlayerHeight: DEFAULTS.miniPlayerHeight,
            lastChannel: null,
        };
    },
    // v5 → v6: language filter widens from `channelLanguageFilter: string|null`
    // to `channelLanguageFilters: string[]`. A returning user with an
    // existing single value carries it forward as a one-item array. Adds
    // the new `channelCountryFilters: string[]` (empty = no filter).
    5: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape> & {
            channelLanguageFilter?: string | null;
        };
        const carryLang = typeof base.channelLanguageFilter === 'string' && base.channelLanguageFilter
            ? [base.channelLanguageFilter]
            : [];
        // Strip the now-removed field so it doesn't linger in localStorage.
        const { channelLanguageFilter: _drop, ...rest } = base;
        void _drop;
        return {
            ...DEFAULTS,
            ...rest,
            channelLanguageFilters: carryLang,
            channelCountryFilters: [],
        };
    },
    // v6 → v7: Spec D adds the `recordings` filter object. Existing blobs
    // inherit the defaults (no statuses selected, no dropdown, no search,
    // sort=date-desc); fresh installs get the same defaults via DEFAULTS.
    6: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
            recordings: { ...RECORDINGS_FILTER_DEFAULTS },
        };
    },
    // v7 → v8: Spec F adds `recordings.storagePanelCollapsed` (default false
    // = panel expanded). Existing v7 blobs already carry every other
    // recordings filter key — preserve them and just inject the new flag.
    7: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        const baseRecordings =
            (base.recordings && typeof base.recordings === 'object'
                ? base.recordings
                : {}) as Partial<RecordingsFilterState>;
        return {
            ...DEFAULTS,
            ...base,
            recordings: {
                ...RECORDINGS_FILTER_DEFAULTS,
                ...baseRecordings,
                storagePanelCollapsed:
                    typeof baseRecordings.storagePanelCollapsed === 'boolean'
                        ? baseRecordings.storagePanelCollapsed
                        : false,
            },
        };
    },
    // v8 → v9: Phase post-4.10 adds `recommendedEpgPanelCollapsed: boolean`
    // (default false = panel expanded on first admin visit to /sources).
    // Existing v8 blobs default false, preserving the "show this panel by
    // default" UX for returning admins.
    8: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
            recommendedEpgPanelCollapsed: false,
        };
    },
    // v9 → v10: Guide group now defaults to Favourites instead of the
    // placeholder/empty-state path. Preserve explicit user selections, but
    // migrate old null/default blobs to the new first-visit group.
    9: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
            epgSelectedGroup: base.epgSelectedGroup ?? DEFAULTS.epgSelectedGroup,
        };
    },
    // v10 → v11: Atlas Phase 7 backfills `channelCountryFiltersSaved` for blobs
    // written before Phase 6 (the shared region saved-default). The `...DEFAULTS`
    // spread fills any key absent from the stored blob — the same-version read
    // path returns `data` verbatim, so a fresh key only reaches existing users
    // via this migration. (The Guide's old `epgRegionFilter` was removed in R5
    // when the region facet became the shared `channelCountryFilters`.)
    10: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v11 → v12: Atlas remediation R3 adds the channel TAGS facet
    // (`channelTagFilters` / `channelTagFiltersSaved`). The `...DEFAULTS` spread
    // backfills the new arrays for blobs written before R3.
    11: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v12 → v13: adds the measured-quality facet (`channelMinHeight` /
    // `channelMinFps`). Defaults-spread fills them as null (no filter).
    12: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v13 → v14: adds the device-local external-player hand-off
    // (`externalPlayerScheme` / `externalPlayerCustomTemplate`).
    // Defaults-spread fills them ('m3u' = the previous behaviour).
    13: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v14 → v15: adds `navExpanded` (desktop sidebar expand state). Defaults-spread
    // fills it false (collapsed) for blobs written before the nav unification.
    14: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v15 → v16: X4 (ux-remediation #5) adds `recordingFailuresDismissedAt`
    // (Home failed-recording banner dismissal watermark). Defaults-spread
    // fills it 0 (never dismissed) for existing blobs — the banner is
    // additive, so a returning user simply sees it if they have failed rows.
    15: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v17 → v18: adds the measured multi-audio facet
    // (`channelMultiAudioOnly`). Defaults-spread fills it false (no filter),
    // so a blob written before live-audio-track-selection Phase 4 keeps
    // today's channel list.
    17: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
    // v16 → v17: X5 (ux-remediation U4) drops the device-local `volume`
    // field. Nothing read it any more — the player's actual volume moved to
    // the per-user `preferences` store's `defaultVolume`/`rememberVolume`/
    // `lastVolume` (server-authoritative, `/api/me/preferences`), and a
    // second local authority for the same fact is exactly the "two numbers,
    // one fact" trap (root CLAUDE.md rule #2). `...DEFAULTS` spread already
    // omits the field going forward; this migration just discards whatever
    // stale value an existing blob carried.
    16: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape> & { volume?: unknown };
        const { volume: _drop, ...rest } = base;
        void _drop;
        return {
            ...DEFAULTS,
            ...rest,
        };
    },
    // v18 → v19: adds `liveListPaneCollapsed` (Live's large-mobile channel-list
    // pane). Defaults-spread fills it false — the list expanded, which is what
    // every blob written before mobile-foldable-redesign M3 implicitly had,
    // since there was no pane to collapse.
    18: (prev: unknown): FiltersShape => {
        const base = (prev && typeof prev === 'object' ? prev : {}) as Partial<FiltersShape>;
        return {
            ...DEFAULTS,
            ...base,
        };
    },
};

export const useFiltersStore = defineStore('filters', () => {
    const data = ref<FiltersShape>(
        loadPersisted<FiltersShape>({
            key: KEY,
            version: VERSION,
            migrations: MIGRATIONS,
            fallback: () => ({ ...DEFAULTS }),
            legacyKeys: LEGACY_KEYS,
        }),
    );

    let saveTimer: ReturnType<typeof setTimeout> | null = null;

    watch(
        data,
        (next) => {
            if (saveTimer !== null) clearTimeout(saveTimer);
            saveTimer = setTimeout(() => {
                savePersisted(KEY, VERSION, next);
            }, 200);
        },
        { deep: true },
    );

    function reset() {
        data.value = { ...DEFAULTS };
        log.info('filters.reset');
    }

    function patch(p: Partial<FiltersShape>) {
        const prevRecordings = data.value.recordings;
        data.value = { ...data.value, ...p };
        // Spec D — diagnostic trail for the Recordings filter so console-
        // debug users can see the active state. Logged once per change at
        // the store-write boundary (NOT per keystroke — the component
        // debounces upstream).
        if (p.recordings && p.recordings !== prevRecordings) {
            log.debug('recordings.filter.changed', {
                statuses: p.recordings.statuses,
                channelKey: p.recordings.channelKey,
                sourceId: p.recordings.sourceId,
                searchLen: p.recordings.search.length,
                sort: p.recordings.sort,
            });
        }
    }

    /** Phase 2.6 — clamp on every write so callers can't bypass the 150..500 range. */
    function setEpgSidebarWidth(px: number) {
        data.value = { ...data.value, epgSidebarWidth: clampSidebar(px) };
    }

    return { data, reset, patch, setEpgSidebarWidth };
});
